// src/utils/imageUtils.js
import API from "../services/api";

/**
 * ✅ Retourne l'origin du backend
 */
const getBackendOrigin = () => {
  // 1. Essayer REACT_APP_BACKEND_URL
  const envBackend =
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_BACKEND ||
    "";

  if (envBackend) {
    try {
      return new URL(envBackend).origin;
    } catch {}
  }

  // 2. Essayer REACT_APP_API_URL
  const apiUrl = process.env.REACT_APP_API_URL || "";
  if (apiUrl) {
    try {
      // Enlever /api à la fin pour obtenir l'origin
      const url = new URL(apiUrl);
      return url.origin;
    } catch {}
  }

  // 3. Essayer API.defaults.baseURL
  const base = API?.defaults?.baseURL || "";
  if (base) {
    try {
      // Si c'est une URL complète
      if (base.startsWith('http://') || base.startsWith('https://')) {
        return new URL(base).origin;
      }
    } catch {}
  }

  // 4. Détection automatique selon le hostname
  const hostname = window.location.hostname;
  const isDev = process.env.NODE_ENV === 'development';
  
  if (isDev || hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  
  // En production : deduire le backend depuis le frontend.
  // app2.maxula.ma -> https://api2.maxula.ma
  if (hostname.startsWith('app2.')) {
    return `https://${hostname.replace(/^app2\./, 'api2.')}`;
  }
  if (hostname.startsWith('app.')) {
    return `https://${hostname.replace(/^app\./, 'api.')}`;
  }
  if (hostname === 'maxula.ma' || hostname === 'www.maxula.ma') {
    return 'https://api2.maxula.ma';
  }
  // Tout autre hôte maxula.ma (deals.maxula.ma, etc.) -> backend prod
  if (hostname.endsWith('.maxula.ma')) {
    return 'https://api2.maxula.ma';
  }

  // 5. Fallback
  return window.location.origin;
};

const BACKEND_ORIGIN = getBackendOrigin();

// 🔍 Debug - Afficher l'URL du backend détectée
console.log('🖼️ Backend Origin:', BACKEND_ORIGIN);

/**
 * ✅ Normalise un chemin d'image pour l'affichage (localhost + serveur)
 */
export const normalizeImagePath = (img) => {
  // Gestion des objets
  if (img && typeof img === "object") {
    const candidates = ["url", "path", "filename", "src", "location"];
    for (const k of candidates) {
      if (img[k]) return normalizeImagePath(img[k]);
    }
    return "";
  }

  if (!img || typeof img !== "string") return "";

  let p = img.trim();
  if (!p) return "";

  // URL complète / blob / data
  if (
    /^https?:\/\//i.test(p) ||
    p.startsWith("blob:") ||
    p.startsWith("data:image/")
  ) {
    return p;
  }

  // Windows path -> normalise
  p = p.replaceAll("\\", "/");

  // Si on a un chemin complet contenant /uploads/
  if (p.includes("/uploads/")) {
    p = p.split("/uploads/")[1];
  }

  // Chemins absolus
  if (p.startsWith("/api/uploads/")) return `${BACKEND_ORIGIN}${p}`;
  if (p.startsWith("/uploads/")) return `${BACKEND_ORIGIN}/api${p}`;

  // Nettoyage si "api/uploads/xxx" ou "uploads/xxx" ou juste "xxx.png"
  p = p.replace(/^\/?api\/uploads\//, "");
  p = p.replace(/^\/?uploads\//, "");
  p = p.replace(/^\/+/, "");

  return `${BACKEND_ORIGIN}/api/uploads/${p}`;
};

/**
 * ✅ Extrait le filename depuis un path/url
 */
export const getFilenameFromPath = (path) => {
  if (!path || typeof path !== "string") return "";

  try {
    if (/^https?:\/\//i.test(path)) {
      const url = new URL(path);
      path = url.pathname;
    }
    const segments = path.split("/").filter(Boolean);
    return segments[segments.length - 1] || "";
  } catch {
    return "";
  }
};

/**
 * ✅ Vérifie si un url est valide (utilisé dans tes pages)
 */
export const isValidImageUrl = (url) => {
  if (!url || typeof url !== "string") return false;

  try {
    if (url.startsWith("blob:")) return true;
    if (url.startsWith("data:image/")) return true;

    if (/^https?:\/\//i.test(url)) {
      new URL(url);
      return true;
    }

    if (url.startsWith("/")) return true;

    return false;
  } catch {
    return false;
  }
};

/**
 * ✅ Placeholder (utilisé dans AdminDashboard / Orders)
 */
export const getPlaceholderImage = (width = 400, height = 300, text = "Image") => {
  const encodedText = encodeURIComponent(text);
  return (
    `data:image/svg+xml,` +
    `%3Csvg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'%3E` +
    `%3Crect fill='%23e5e7eb' width='${width}' height='${height}'/%3E` +
    `%3Ctext x='50%25' y='50%25' font-size='18' text-anchor='middle' fill='%239ca3af'%3E` +
    `${encodedText}%3C/text%3E%3C/svg%3E`
  );
};

// ✅ Export pour debugging
export const getBackendURL = () => BACKEND_ORIGIN;

// ✅ default export (pour compat)
export default normalizeImagePath;